import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Play, Pause, X } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";

const BAR_COUNT = 96;
const PAD_COUNT = 16;

const Waveform = ({ peaks, fraction, onSeek }) => {
  const ref = useRef(null);
  const bars = peaks && peaks.length ? peaks : new Array(BAR_COUNT).fill(0.4);
  const playedIndex = Math.floor(fraction * bars.length);

  const handle = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX ?? (e.touches && e.touches[0]?.clientX)) - rect.left;
    onSeek(Math.max(0, Math.min(x / rect.width, 1)));
  };

  return (
    <div
      ref={ref}
      onClick={handle}
      role="slider"
      aria-label="Seek preview"
      aria-valuenow={Math.round(fraction * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      data-testid="waveform-scrubber"
      className="flex items-end gap-[2px] h-10 cursor-pointer w-full"
    >
      {bars.map((p, i) => {
        const isPlayhead = i === playedIndex;
        const color = isPlayhead ? "var(--rose)" : i < playedIndex ? "var(--gold)" : "rgba(237,224,200,.22)";
        return (
          <span
            key={i}
            style={{
              flex: 1,
              height: `${Math.max(10, p * 100)}%`,
              background: color,
              borderRadius: "1px",
              transition: "background-color .12s linear",
            }}
          />
        );
      })}
    </div>
  );
};

const PadLights = ({ bpm, playing }) => {
  const [active, setActive] = useState(-1);
  const raf = useRef();
  const reduce = useReducedMotion();

  useEffect(() => {
    if (!playing || reduce) {
      setActive(-1);
      return undefined;
    }
    const stepMs = 60000 / (bpm || 90) / 2; // eighth notes
    const loop = () => {
      const idx = Math.floor(performance.now() / stepMs) % PAD_COUNT;
      setActive(idx);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, [playing, bpm, reduce]);

  return (
    <div className="grid grid-rows-2 grid-flow-col gap-[5px]" data-testid="pad-lights" aria-hidden="true">
      {Array.from({ length: PAD_COUNT }).map((_, i) => (
        <span
          key={i}
          style={{
            width: 14,
            height: 14,
            borderRadius: "2px",
            border: "1px solid rgba(200,155,60,.35)",
            background: i === active ? "var(--gold-lift)" : "rgba(200,155,60,.08)",
            boxShadow: i === active ? "0 0 8px var(--gold)" : "none",
            transition: "background-color .08s linear, box-shadow .08s linear",
          }}
        />
      ))}
    </div>
  );
};

export const ConsolePlayer = () => {
  const player = usePlayer();
  const reduce = useReducedMotion();
  const active = player?.hasStarted && player?.track;
  useEffect(() => {
    document.body.classList.toggle("player-active", Boolean(active));
    return () => document.body.classList.remove("player-active");
  }, [active]);
  if (!player) return null;
  const { track, isPlaying, fraction, hasStarted, toggle, seek, span } = player;
  if (!hasStarted || !track) return null;

  const mmss = (secs) => {
    const s = Math.floor(secs);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };
  const cap = span || 30;

  return (
    <AnimatePresence>
      <motion.div
        key="console"
        data-testid="console-player"
        initial={reduce ? false : { y: "100%" }}
        animate={{ y: 0 }}
        exit={{ y: "100%" }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed bottom-0 inset-x-0 z-50"
        style={{ backgroundColor: "var(--ink-raised)", borderTop: "1px solid var(--gold)" }}
      >
        <div className="mx-auto max-w-[1360px] px-3 sm:px-6 py-3 flex items-center gap-4">
          {/* Left: artwork + candle glow */}
          <div className="relative shrink-0" style={{ width: 56, height: 56 }}>
            {isPlaying && (
              <span
                className="candle-glow absolute -inset-1"
                style={{ background: "radial-gradient(circle, rgba(200,155,60,.7), transparent 70%)", borderRadius: "3px" }}
              />
            )}
            <img
              src={track.artwork}
              alt={track.title}
              className="relative w-14 h-14 object-cover"
              style={{ borderRadius: "var(--radius)", border: "1px solid rgba(200,155,60,.4)" }}
            />
          </div>

          {/* Play / pause */}
          <button
            onClick={toggle}
            data-testid="console-toggle"
            aria-label={isPlaying ? "Pause" : "Play"}
            className="shrink-0 grid place-items-center transition-colors"
            style={{ width: 44, height: 44, borderRadius: "50%", border: "1px solid var(--gold)", color: "var(--gold)" }}
          >
            {isPlaying ? <Pause size={18} fill="currentColor" /> : <Play size={18} fill="currentColor" />}
          </button>

          {/* Center: title + waveform */}
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <p className="font-caps truncate" style={{ fontSize: ".68rem", color: "var(--gold)" }}>{track.title}</p>
                <p className="truncate" style={{ fontSize: ".72rem", color: "var(--ash)" }}>{track.artist}</p>
              </div>
              <span className="hidden sm:block shrink-0" style={{ fontSize: ".7rem", color: "var(--ash)" }}>
                {mmss(fraction * cap)} / {mmss(cap)} · {track.full ? "full song" : "preview"}
              </span>
            </div>
            {/* full waveform on >= sm, thin line on mobile */}
            <div className="hidden sm:block mt-1">
              <Waveform peaks={track.peaks} fraction={fraction} onSeek={seek} />
            </div>
            <div className="sm:hidden mt-2 h-[3px] w-full" style={{ background: "rgba(237,224,200,.18)" }}>
              <div style={{ height: "100%", width: `${fraction * 100}%`, background: "var(--gold)" }} />
            </div>
          </div>

          {/* Right: pad lights (desktop only) */}
          <div className="hidden lg:block shrink-0">
            <PadLights bpm={track.bpm} playing={isPlaying} />
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
